/**
 * Диспетчеры: профиль, график, лимиты, SIP-аккаунт.
 *
 * Дни недели — ISO 1..7 (пн=1) везде: в базе, в SQL и здесь. Смена, у которой
 * конец меньше начала, — ночная, через полночь (§6.1).
 * SIP-пароль показывается один раз при создании или ротации: второй копии нет,
 * он живёт только в asterisk.ps_auths (§7.8).
 */
import { z } from 'zod';
import { bigIdSchema, businessDateSchema, isoDateTimeSchema, roleSchema } from './common.dto.js';
/** Время в графике — МСК, HH:mm */
export const shiftTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'ожидается время HH:mm');
export const weekdaySchema = z.number().int().min(1).max(7);
export const scheduleDaySchema = z
    .object({
    weekday: weekdaySchema,
    isWorking: z.boolean(),
    start: shiftTimeSchema.nullable(),
    end: shiftTimeSchema.nullable(),
})
    .refine((d) => !d.isWorking || (d.start !== null && d.end !== null), {
    message: 'у рабочего дня обязаны быть начало и конец смены',
});
/** Исключение на дату перекрывает недельный шаблон целиком (§6.1) */
export const scheduleExceptionSchema = z
    .object({
    date: businessDateSchema,
    isWorking: z.boolean(),
    start: shiftTimeSchema.nullable(),
    end: shiftTimeSchema.nullable(),
    reason: z.string().max(300).nullable(),
})
    .refine((d) => !d.isWorking || (d.start !== null && d.end !== null), {
    message: 'у рабочего дня обязаны быть начало и конец смены',
});
/** Лимиты раздачи. Арбитр дневного лимита — первичка, витрина им не является (§6.2) */
export const dispatcherProfileSchema = z.object({
    dailyLimit: z.number().int().min(0).max(2000),
    maxOpen: z.number().int().min(1).max(20),
    allowFresh: z.boolean(),
    allowNdz: z.boolean(),
    /** Личная ставка за заявку, ₽; null — общая ставка из настроек */
    payPerLeadRub: z.number().int().min(0).max(100000).nullable(),
    /** Оклад за смену, ₽; null — оклада нет */
    payShiftRub: z.number().int().min(0).max(100000).nullable(),
    /** Норма на смену: минут на линии и наборов; null — не проверяется */
    normActiveMinutes: z.number().int().min(0).max(1440).nullable(),
    normCalls: z.number().int().min(0).max(5000).nullable(),
});
export const userRowSchema = z.object({
    id: bigIdSchema,
    login: z.string(),
    fullName: z.string(),
    role: roleSchema,
    isActive: z.boolean(),
    profile: dispatcherProfileSchema.nullable(),
    schedule: z.array(scheduleDaySchema),
    /** Присутствие: линия, готовность и когда последний раз видели сокет */
    sipRegistered: z.boolean(),
    ready: z.boolean(),
    wsSeenAt: isoDateTimeSchema.nullable(),
    sipEndpointId: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const usersQuerySchema = z.object({
    role: roleSchema.optional(),
    isActive: z.enum(['true', 'false']).optional(),
});
export const createUserBodySchema = z.object({
    login: z.string().min(3).max(64),
    fullName: z.string().min(3).max(200),
    role: roleSchema,
    password: z.string().min(10).max(200),
    profile: dispatcherProfileSchema.optional(),
    schedule: z.array(scheduleDaySchema).max(7).optional(),
});
export const updateUserBodySchema = z.object({
    fullName: z.string().min(3).max(200).optional(),
    role: roleSchema.optional(),
    isActive: z.boolean().optional(),
    profile: dispatcherProfileSchema.partial().optional(),
});
/** PUT /api/users/:id/schedule — недельный шаблон целиком, не по одному дню */
export const putScheduleBodySchema = z.object({
    days: z.array(scheduleDaySchema).max(7),
});
export const putScheduleExceptionBodySchema = scheduleExceptionSchema;
/** Сброс пароля админом: старый не спрашиваем, действие пишется в audit_log */
export const resetPasswordBodySchema = z.object({
    newPassword: z.string().min(10).max(200),
});
/**
 * POST /api/users/:id/sip — завести SIP-аккаунт диспетчеру.
 * Пароль в ответе показывается один раз и нигде не хранится у нас.
 */
export const provisionSipResponseSchema = z.object({
    endpointId: z.string(),
    password: z.string(),
    /** Ротация разрешена только офлайн-диспетчеру: иначе тишина до re-REGISTER (§7.8) */
    rotatedAt: isoDateTimeSchema.nullable(),
});
//# sourceMappingURL=users.dto.js.map